const DEPLOYMENT_MODES = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
  HYBRID: "HYBRID",
};

const STORAGE_KEYS = {
  deploymentModeOverride: "clearhealthDeploymentModeOverride",
  localApiBaseUrl: "clearhealthLocalApiBaseUrl",
  localApiToken: "clearhealthLocalApiToken",
  cloudFacilitySession: "clearhealthCloudFacilitySession",
};

const normalizeMode = (value) => {
  const mode = String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (mode === "HYBRID" || mode === "HYBRID_LOCAL_SERVER" || mode === "HYBRID_MODE") return DEPLOYMENT_MODES.HYBRID;
  if (mode === "OFFLINE" || mode === "LOCAL" || mode === "LOCAL_EDITION" || mode === "OFFLINE_ONLY") return DEPLOYMENT_MODES.OFFLINE;
  if (mode === "ONLINE" || mode === "CLOUD" || mode === "CLOUD_ONLY" || mode === "ONLINE_ONLY") return DEPLOYMENT_MODES.ONLINE;
  return "";
};

const readJsonStorage = (storage, key) => {
  try {
    const raw = storage?.getItem?.(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getStoredLocalApiSession = () => {
  if (typeof window === "undefined") return null;
  return readJsonStorage(window.localStorage, "clearhealthLocalApiSession");
};

const hasLocalApiToken = () => {
  if (typeof window === "undefined") return false;
  return !!(
    window.localStorage.getItem(STORAGE_KEYS.localApiToken) ||
    window.sessionStorage.getItem(STORAGE_KEYS.localApiToken)
  );
};

const hasActiveLocalApiSession = () => {
  const localSession = getStoredLocalApiSession();
  const localMode = normalizeMode(localSession?.hospital?.deploymentMode);
  return hasLocalApiToken() && localSession?.authProvider === "local-api" &&
    (localMode === DEPLOYMENT_MODES.OFFLINE || localMode === DEPLOYMENT_MODES.HYBRID);
};

const hasCloudFacilitySession = () => {
  if (typeof window === "undefined") return false;
  if (window.sessionStorage.getItem(STORAGE_KEYS.cloudFacilitySession) === "true") return true;
  const user = readJsonStorage(window.sessionStorage, "userData");
  return Boolean(user?.isPlatformOperator || user?.activeFacilityId);
};

const isDevServerOrigin = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const port = String(window.location.port || "");
  return ["localhost", "127.0.0.1", "::1"].includes(host) && ["3000", "3001"].includes(port);
};

export const isLocalApplianceOrigin = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const port = String(window.location.port || "");
  if (!["localhost", "127.0.0.1", "::1"].includes(host)) return false;
  return port === "" || port === "80" || port === "443";
};

const getCurrentFacility = () => {
  if (typeof window === "undefined") return null;
  const localSession = getStoredLocalApiSession();
  if (hasCloudFacilitySession()) {
    return (
      readJsonStorage(window.sessionStorage, "hospitalDetails") ||
      readJsonStorage(window.localStorage, "hospitalDetails") ||
      null
    );
  }
  if (hasActiveLocalApiSession()) return localSession?.hospital || null;
  return (
    readJsonStorage(window.sessionStorage, "hospitalDetails") ||
    readJsonStorage(window.localStorage, "hospitalDetails") ||
    localSession?.hospital ||
    null
  );
};

export const getDeploymentMode = () => {
  const defaultMode = getDeploymentModeDefault();
  if (defaultMode === DEPLOYMENT_MODES.OFFLINE || defaultMode === DEPLOYMENT_MODES.HYBRID) {
    return defaultMode;
  }

  if (hasCloudFacilitySession()) return DEPLOYMENT_MODES.ONLINE;

  if (typeof window !== "undefined") {
    const override = getDeploymentModeOverride();
    if (override) return override;
  }

  if (isDevServerOrigin() && !hasActiveLocalApiSession()) return DEPLOYMENT_MODES.ONLINE;

  const facility = getCurrentFacility();
  return normalizeMode(facility?.deploymentMode) || defaultMode || DEPLOYMENT_MODES.ONLINE;
};

export const getDeploymentModeOverride = () => {
  if (typeof window === "undefined") return "";
  return normalizeMode(window.localStorage.getItem(STORAGE_KEYS.deploymentModeOverride));
};

export const getDeploymentModeDefault = () => normalizeMode(process.env.REACT_APP_DEPLOYMENT_MODE_DEFAULT);

export const isLocalEditionBuild = () => {
  const mode = getDeploymentModeDefault();
  if (mode === DEPLOYMENT_MODES.OFFLINE || mode === DEPLOYMENT_MODES.HYBRID) return true;
  const configuredBase = String(process.env.REACT_APP_LOCAL_API_BASE_URL || "").trim();
  return !!configuredBase && !/^https?:\/\//i.test(configuredBase);
};

export const isOnlineMode = () => getDeploymentMode() === DEPLOYMENT_MODES.ONLINE;
export const isOfflineMode = () => getDeploymentMode() === DEPLOYMENT_MODES.OFFLINE;
export const isHybridMode = () => getDeploymentMode() === DEPLOYMENT_MODES.HYBRID;
export const usesLocalApi = () => {
  if (isLocalEditionBuild()) return true;
  if (hasCloudFacilitySession()) return false;
  if (hasActiveLocalApiSession()) return true;
  if (isDevServerOrigin() && !getDeploymentModeOverride()) return false;

  const mode = getDeploymentMode();
  if (mode === DEPLOYMENT_MODES.OFFLINE || mode === DEPLOYMENT_MODES.HYBRID) return true;
  if (mode === DEPLOYMENT_MODES.ONLINE) return false;

  return false;
};

export const getLocalApiBaseUrl = () => {
  const configuredBase = String(process.env.REACT_APP_LOCAL_API_BASE_URL || "").trim();
  const localEditionBuild = isLocalEditionBuild();
  const applianceOrigin = isLocalApplianceOrigin();
  if (applianceOrigin) return "/api";
  if (typeof window !== "undefined") {
    const override = window.localStorage.getItem(STORAGE_KEYS.localApiBaseUrl);
    const normalizedOverride = String(override || "").trim().replace(/\/+$/, "");
    const staleLocalhostOverride = /^https?:\/\/(localhost|127\.0\.0\.1):4100\/api$/i.test(normalizedOverride);
    if (normalizedOverride && !((localEditionBuild || applianceOrigin) && staleLocalhostOverride)) return normalizedOverride;
  }
  if (configuredBase) return configuredBase.replace(/\/+$/, "");
  if (localEditionBuild) return "/api";
  return "http://localhost:4100/api";
};

export const getAuthToken = () => {
  if (typeof window === "undefined") return "";
  return (
    window.localStorage.getItem(STORAGE_KEYS.localApiToken) ||
    window.sessionStorage.getItem(STORAGE_KEYS.localApiToken) ||
    ""
  );
};

export const setAuthToken = (token, { persist = true } = {}) => {
  if (typeof window === "undefined") return;
  const storage = persist ? window.localStorage : window.sessionStorage;
  storage.setItem(STORAGE_KEYS.localApiToken, token || "");
};

export const clearAuthToken = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEYS.localApiToken);
  window.sessionStorage.removeItem(STORAGE_KEYS.localApiToken);
};

export const deploymentStorageKeys = STORAGE_KEYS;
export const deploymentModes = DEPLOYMENT_MODES;
